import { env } from "config";
import { useAuth } from "hooks/useAuth";

/**
 * Converts an Azurite blob storage URL to use the Vite proxy in development.
 * In development, replaces http://localhost:10000 with /azurite to use the proxy.
 * In production, returns the URL as-is.
 * @param {string} url - The blob storage URL
 * @returns {string} - The converted URL
 */
const convertBlobUrl = (url) => {
  if (!url) return url;

  const isDevelopment = import.meta.env.VITE_REACT_APP_ENVIRONMENT === "Local";
  const azuriteLocalUrl = "http://localhost:10000";
  const proxyPath = "/azurite";

  if (isDevelopment && url.includes(azuriteLocalUrl)) {
    return url.replace(azuriteLocalUrl, proxyPath);
  }

  return url;
};

async function downloadFromUrl(url, filename) {
  try {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Failed to download file: ${res.status}`);
    }

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "";
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    console.error("downloadFromUrl error:", error);
  }
}

const createAuthenticatedFetch = (ensureValidToken) => {
  return async (url, options = {}) => {
    try {
      const validToken = await ensureValidToken();

      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${validToken}`,
        },
      });

      return response;
    } catch (error) {
      if (error.message && error.message.includes("Authentication required")) {
        throw error;
      }

      console.error("Authenticated fetch failed:", error);
      throw error;
    }
  };
};

export const osirisApi = () => {
  const { ensureValidToken } = useAuth();
  const authenticatedFetch = createAuthenticatedFetch(ensureValidToken);

  return {
    /* DATASET */
    getDatasets: async ({
      onlyReady = false,
      includeUserTransitiveGroups = false,
    } = {}) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/dataset?onlyReady=${onlyReady}&includeUserTransitiveGroups=${includeUserTransitiveGroups}`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    downloadInputFiles: async ({ id, datasetTypes }) => {
      try {
        const selectedDatasetTypes = (datasetTypes ?? []).map(
          (value) => Number(value)
        );

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/dataset/downloadinputs/${id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(selectedDatasetTypes),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw {
            status: response.status,
            ...errorData,
          };
        }

        const links = await response.json();

        if (Array.isArray(links.fileLinks)) {
          links.fileLinks.forEach((file) => {
            const proxiedUrl = convertBlobUrl(file.url);
            downloadFromUrl(proxiedUrl, file.fileName);
          });
        }

        return links;
      } catch (error) {
        console.error(
          "Error during computation input files download:",
          error
        );
        throw error;
      }
    },

    addDataset: async (data) => {
      const formData = new FormData();
      formData.append("description", data.description);

      if (data.staticData) formData.append("staticData", data.staticData);
      if (data.pd) formData.append("pd", data.pd);
      if (data.lgd) formData.append("lgd", data.lgd);
      if (data.cashflows) formData.append("cashflows", data.cashflows);

      const response = await authenticatedFetch(
        `${env.gatewayUrl}/osiris/dataset`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw {
          status: response.status,
          ...errorData,
        };
      }

      return await response.json();
    },

    updateDataset: async (data) => {
      try {
        const formData = new FormData();
        formData.append("Description", data.description);

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/dataset/${data.id}`,
          {
            method: "PATCH",
            body: formData,
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    deleteDataset: async (id) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/dataset/${id}`,
          {
            method: "DELETE",
          }
        );
        return response.ok;
      } catch (error) {
        throw error;
      }
    },

    /* COMPUTATION */
    getComputations: async ({
      onlyReady = false,
      includeUserTransitiveGroups = false,
    } = {}) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/computation?onlyReady=${onlyReady}&includeUserTransitiveGroups=${includeUserTransitiveGroups}`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getComputation: async ({
      id,
      includeUserTransitiveGroups = false,
    } = {}) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/computation/${id}?includeUserTransitiveGroups=${includeUserTransitiveGroups}`,
          {
            method: "GET",
          }
        );
        return await response.text();
      } catch (error) {
        throw error;
      }
    },

    addComputation: async (data) => {
      try {
        const formData = new FormData();

        formData.append("Description", data.description || "");
        formData.append("DatasetId", data.datasetId);
        formData.append("CouponMethod", data.couponMethod);
        formData.append("InterpolationType", data.interpolationType);

        if (data.bucketRules) {
          formData.append("BucketRules", JSON.stringify(data.bucketRules));
        }
        if (data.actuarialRateRules) {
          formData.append(
            "ActuarialRateRules",
            JSON.stringify(data.actuarialRateRules)
          );
        }

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/computation`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw {
            status: response.status,
            ...errorData,
          };
        }

        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    updateComputation: async (data) => {
      try {
        const formData = new FormData();
        formData.append("DatasetId", data.datasetId);
        formData.append("Description", data.description);

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/computation/${data.id}`,
          {
            method: "PATCH",
            body: formData,
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    deleteComputation: async (id) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/computation/${id}`,
          {
            method: "DELETE",
          }
        );
        return response.ok;
      } catch (error) {
        throw error;
      }
    },

    /* VARIATION ANALYSIS */
    getVariationAnalysis: async ({
      id,
      includeUserTransitiveGroups = false,
    } = {}) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/variation-analysis/${id}?includeUserTransitiveGroups=${includeUserTransitiveGroups}`,
          {
            method: "GET",
          }
        );
        return await response.text();
      } catch (error) {
        throw error;
      }
    },

    getVariations: async ({
      onlyReady = false,
      includeUserTransitiveGroups = false,
    } = {}) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/variation-analysis?onlyReady=${onlyReady}&includeUserTransitiveGroups=${includeUserTransitiveGroups}`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    deleteVariationAnalysis: async (id) => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/variation-analysis/${id}`,
          {
            method: "DELETE",
          }
        );
        return response.ok;
      } catch (error) {
        throw error;
      }
    },

    addVariationAnalysis: async (data) => {
      try {
        const formData = new FormData();

        formData.append("Description", data.description);
        formData.append("DatasetDepartureId", data.datasetDepartureId);
        formData.append("DatasetArrivalId", data.datasetArrivalId);
        formData.append("CouponMethod", data.couponMethod);
        formData.append("InterpolationType", data.interpolationType);

        if (data.bucketRulesDeparture) {
          formData.append("BucketRulesDeparture", data.bucketRulesDeparture);
        }

        if (data.actuarialRateRulesDeparture) {
          formData.append(
            "ActuarialRateRulesDeparture",
            data.actuarialRateRulesDeparture
          );
        }

        if (data.bucketRulesArrival) {
          formData.append("BucketRulesArrival", data.bucketRulesArrival);
        }

        if (data.actuarialRateRulesArrival) {
          formData.append(
            "ActuarialRateRulesArrival",
            data.actuarialRateRulesArrival
          );
        }

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/variation-analysis`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw {
            status: response.status,
            message:
              errorData?.message || errorData?.error || "An error occurred",
            ...errorData,
          };
        }

        return await response.json();
      } catch (error) {
        console.error("Error when adding a variation analysis:", error);
        throw error;
      }
    },

    updateVariationAnalysis: async (data) => {
      try {
        const formData = new FormData();

        formData.append("Description", data.description);

        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/variation-analysis/${data.id}`,
          {
            method: "PATCH",
            body: formData,
          }
        );
        return await response.json();
      } catch (error) {
        console.error("Error when updating a variation analysis:", error);
        throw error;
      }
    },

    /* CHARTS */
    getComputationCharts: async (computationId) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/computation/charts/${computationId}`,
          {
            method: "GET",
          }
        );
        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.json();
      } catch (error) {
        console.error("Error during computation charts retrieval:", error);
        throw error;
      }
    },

    getControlCharts: async (computationId) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/control/charts/${computationId}`,
          {
            method: "GET",
          }
        );
        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.json();
      } catch (error) {
        console.error("Error during control charts retrieval:", error);
        throw error;
      }
    },

    getVariationCharts: async (variationAnalysisId) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/variation-analysis/charts/${variationAnalysisId}`,
          {
            method: "GET",
          }
        );
        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.json();
      } catch (error) {
        console.error("Error during variation charts retrieval:", error);
        throw error;
      }
    },

    getDashboardCharts: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/dashboard`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    /* RESULT */
    getResultHeaderComputation: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/result-header/simple/2`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getResultHeaderControl: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/result-header/simple/3`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getResultHeaderVariationAnalysis: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/result-header/simple/4`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getSelectedComputationResult: async (id) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/computation/result/${id}`,
          { method: "GET" }
        );

        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.text();
      } catch (error) {
        console.error("Error during computation result retrieval:", error);
        throw error;
      }
    },

    getSelectedComputationControl: async (id) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/control/result/${id}`,
          { method: "GET" }
        );

        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.text();
      } catch (error) {
        console.error("Error during control result retrieval:", error);
        throw error;
      }
    },

    getSelectedVariationAnalysis: async (id) => {
      try {
        const res = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/variation-analysis/result/${id}`,
          { method: "GET" }
        );

        if (!res.ok) throw new Error(`API ${res.status}`);

        const json = await res.json();
        const url = json.url;

        const proxiedUrl = convertBlobUrl(url);
        const blobRes = await fetch(proxiedUrl);

        if (!blobRes.ok) throw new Error(`BLOB ${blobRes.status}`);

        return await blobRes.text();
      } catch (error) {
        console.error(
          "Error during variation analysis result retrieval:",
          error
        );
        throw error;
      }
    },

    /* ANALYSIS */
    getPivotHeaderComputation: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/pivot-header/simple/2`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getPivotHeaderControl: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/pivot-header/simple/3`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },

    getPivotHeaderVariationAnalysis: async () => {
      try {
        const response = await authenticatedFetch(
          `${env.gatewayUrl}/osiris/pivot-header/simple/4`,
          {
            method: "GET",
          }
        );
        return await response.json();
      } catch (error) {
        throw error;
      }
    },
  };
};
